import React from "react";
import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import {
  assertFieldName,
  getFieldTypeFromSchema,
  type InferredSubmissionFieldType,
  type PathArray,
  type SubmissionFieldType,
} from "common/utils/formStructure";
import { ActionButton } from "../../../../../../Common/Buttons/ActionButton";
import { CopyEntityID } from "../../../../../../Common/CopyEntityID";
import { Body } from "../../../../../../Common/Typography";
import { fieldTypeToTitleMap } from "../../constants";
import {
  getSchemaFormPath,
  getUiSchemaFormPath,
  useBuilderWatch,
} from "../../form/form";
import { getModuleRequiringField } from "../../modules/utils";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "../../SubmissionsBuilderContext";
import { FieldSettingsHeader } from "./__styles__/FieldSettingsView";
import { CheckboxWidgetSettings } from "./CheckboxWidgetSettings";
import { DamageDegreeWidgetSettings } from "./DamageDegreeWidgetSettings";
import { DateWidgetSettings } from "./DateWidgetSettings";
import { DocumentUploadFieldSettings } from "./DocumentUploadFieldSettings/DocumentUploadFieldSettings";
import { DropdownWidgetSettings } from "./DropdownWidgetSettings/DropdownWidgetSettings";
import { NumberFieldSettings } from "./NumberFieldSettings";
import { NumberWithUnitsFieldSettings } from "./NumberWithUnitsFieldSettings/NumberWithUnitsFieldSettings";
import { ObjectFieldTemplateSettings } from "./ObjectFieldTemplateSettings";
import { PropertyMarketValueFieldSettings } from "./PropertyMarketValueFieldSettings";
import { TagsWidgetSettings } from "./TagsWidgetSettings/TagsWidgetSettings";
import { TextareaWidgetSettings } from "./TextareaWidgetSettings";
import { TextWidgetSettings } from "./TextWidgetSettings";
import { UniqueIdFieldSettings } from "./UniqueIdFieldSettings";
import { UserWidgetSettings } from "./UserWidgetSettings";

export type InputSettingsFormProps = {
  fieldPath: PathArray;
  isReadOnly: boolean;
  isModuleInput: boolean;
  inputType: string | null;
};

function InvalidFieldType({ inputType }: InputSettingsFormProps) {
  return <div>Invalid input type: {inputType}</div>;
}

const fieldTypeToSettingsFormMap: Record<
  SubmissionFieldType,
  React.ComponentType<InputSettingsFormProps>
> = {
  checkbox: CheckboxWidgetSettings,
  DamageDegree: DamageDegreeWidgetSettings,
  date: DateWidgetSettings,
  DocumentUploader: DocumentUploadFieldSettings,
  group: ObjectFieldTemplateSettings,
  multiSelect: DropdownWidgetSettings,
  number: NumberFieldSettings,
  NumberWithUnits: NumberWithUnitsFieldSettings,
  PropertyMarketValue: PropertyMarketValueFieldSettings,
  select: DropdownWidgetSettings,
  Tags: TagsWidgetSettings,
  text: TextWidgetSettings,
  textarea: TextareaWidgetSettings,
  UniqueId: UniqueIdFieldSettings,
  User: UserWidgetSettings,
};

function getFieldSettingsFormComponent(fieldType: InferredSubmissionFieldType) {
  if (fieldType === "unknown") {
    return InvalidFieldType;
  }

  return fieldTypeToSettingsFormMap[fieldType];
}

function getFieldTypeTitle(fieldType: InferredSubmissionFieldType) {
  if (fieldType === "unknown") {
    return "Unknown";
  }

  return fieldTypeToTitleMap[fieldType];
}

type FieldSettingsViewProps = {
  selectedField: PathArray;
};

export function FieldSettingsView({ selectedField }: FieldSettingsViewProps) {
  const [schema, uiSchema] = useBuilderWatch([
    getSchemaFormPath(selectedField),
    getUiSchemaFormPath(selectedField),
  ]) as [RJSFSchema | undefined, UiSchema | undefined];

  if (schema && uiSchema) {
    return (
      <FieldSettings
        selectedField={selectedField}
        schema={schema}
        uiSchema={uiSchema}
      />
    );
  }

  return <UnknownField />;
}

type FieldSettingsProps = {
  selectedField: PathArray;
  schema: RJSFSchema;
  uiSchema: UiSchema;
};

function FieldSettings({
  selectedField,
  schema,
  uiSchema,
}: FieldSettingsProps) {
  const { selectField } = useSubmissionsBuilderContext();
  const modules = useBuilderWatch("modules");

  const fieldType = getFieldTypeFromSchema(schema, uiSchema);
  const title = getFieldTypeTitle(fieldType);
  const FieldSettingsForm = getFieldSettingsFormComponent(fieldType);

  const [mode] = useSubmissionsBuilderMode();
  const isReadOnly = mode !== SUBMISSIONS_BUILDER_MODE.BUILD;

  const fieldName = assertFieldName(selectedField);
  const isModuleInput =
    fieldType === "group" ? false : (
      Boolean(getModuleRequiringField(fieldName, modules))
    );

  return (
    <>
      <FieldSettingsHeader role="none">
        <ActionButton
          icon="chevron-left"
          onPress={() => {
            selectField(undefined);
          }}
          aria-label="Back to form fields"
        />
        <Body size="large" type="emphasis">
          {title} field properties
        </Body>
        <CopyEntityID
          entityId={selectedField.join(".")}
          tooltipText="Copy form data path"
        />
      </FieldSettingsHeader>
      <FieldSettingsForm
        fieldPath={selectedField}
        inputType={fieldType}
        isReadOnly={isReadOnly}
        isModuleInput={isModuleInput}
      />
    </>
  );
}

function UnknownField() {
  const { selectField } = useSubmissionsBuilderContext();

  return (
    <FieldSettingsHeader role="none">
      <ActionButton
        icon="chevron-left"
        onPress={() => {
          selectField(undefined);
        }}
        aria-label="Back to form fields"
      />
      <Body size="large" type="emphasis">
        Unknown field selected
      </Body>
    </FieldSettingsHeader>
  );
}
